import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Ban,
  Calendar as CalendarIcon,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  FileText,
  History as HistoryIcon,
  IdCard,
  Pencil,
  Pill,
  Printer,
  RefreshCw,
  Stethoscope,
  Trash2,
  UserCircle2,
} from 'lucide-react';
import {
  MAX_RESULT_UPLOAD_BYTES,
  EPISODE_STATUS_LABELS,
  EPISODE_STATUS_VARIANTS,
  EpisodeTransitions,
  PATIENT_FLOW_LABELS,
  PATIENT_FLOW_ORDER,
  PATIENT_FLOW_VARIANTS,
  ProcedureIdSchema,
  requiredDocsForEpisode,
  requiresAnesthesia,
  requiresExpense,
  hasPermission,
  type EpisodeStatus,
  type PatientFlowStatus,
  type Procedure,
  type ProcedureId,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge, BadgeDot } from '@/components/ui/badge';
import { AuthenticatedImage } from '@/components/ui/authenticated-image';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { avatarGradient } from '@/components/ui/avatar';
import { ApiClientError } from '@/shared/api/client';
import { useAuthStore } from '@/shared/store/auth';
import { ageFromBirthdate, formatShortDateEs, initials } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { usePageTitle } from '@/shared/store/page';
import { usePatientById } from '@/features/patients/api';
import { useUsersList } from '@/features/users/api';
import { useAuditList } from '@/features/audit/api';
import { useToast } from '@/shared/toast/toast-context';
import { useNavigate } from 'react-router-dom';
import { useConsentByProcedure } from '@/features/consent/api';
import { useAnesthesiaByProcedure } from '@/features/anesthesia/api';
import { useExpenseByProcedure } from '@/features/expense/api';
import {
  resultDownloadUrl,
  useDeleteResult,
  useResultsByProcedure,
  useUploadResultFile,
} from '@/features/results/api';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EpisodeStepper } from './EpisodeStepper';
import {
  useCancelProcedure,
  useDeleteProcedure,
  useProcedureById,
  useTransitionProcedure,
  useUpdatePatientFlow,
} from './api';

const TERMINAL_STATES: ReadonlySet<EpisodeStatus> = new Set(['cerrado', 'anulado']);

function isProcedureId(value: string | undefined): value is ProcedureId {
  if (value === undefined) return false;
  return ProcedureIdSchema.safeParse(value).success;
}

export function ProcedureDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const validatedId = isProcedureId(idParam) ? idParam : undefined;

  const { data: procedure, isLoading, isError, error, refetch } = useProcedureById(validatedId);

  usePageTitle(
    procedure !== undefined ? `Episodio ${procedure.id}` : 'Procedimiento',
    'Detalle del episodio clínico',
  );

  if (validatedId === undefined) {
    return <ErrorState title="Identificador inválido" detail={`"${idParam ?? ''}" no es un id de episodio válido.`} />;
  }

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (isError || procedure === undefined) {
    return (
      <ErrorState
        title={isError ? 'No fue posible cargar el episodio' : 'Episodio no encontrado'}
        detail={isError ? error?.message ?? 'Error inesperado' : `El identificador ${validatedId} no existe.`}
        onRetry={isError ? () => void refetch() : undefined}
      />
    );
  }

  return <ProcedureDetailLoaded procedure={procedure} />;
}

/* -------------------------------------------------------------------------- */
/* Loaded view                                                                */
/* -------------------------------------------------------------------------- */

interface LoadedProps {
  procedure: Procedure;
}

function ProcedureDetailLoaded({ procedure }: LoadedProps): JSX.Element {
  const patientQuery = usePatientById(procedure.pacienteId);
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const transitionMutation = useTransitionProcedure();

  const userById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersQuery.data?.items ?? []) {
      map.set(u.id, u.nombre);
    }
    return map;
  }, [usersQuery.data]);

  const isTerminal = TERMINAL_STATES.has(procedure.estado);
  const allowedNext = (EpisodeTransitions[procedure.estado] ?? []).filter(
    (s) => s !== 'anulado',
  );

  const variant = EPISODE_STATUS_VARIANTS[procedure.estado];
  const stateLabel = EPISODE_STATUS_LABELS[procedure.estado];

  /**
   * Click en un hito del stepper. Si requiere firma (consentimiento / anestesia
   * / gasto), navega a la página de captura. La transición de cierre
   * (cerrado) se ejecuta inline con toast de confirmación — no requiere
   * documento.
   */
  const handleStepClick = async (to: EpisodeStatus): Promise<void> => {
    if (to === 'consentimiento') {
      navigate(`/consentimiento/${procedure.id}`);
      return;
    }
    if (to === 'anestesia') {
      navigate(`/anestesia/${procedure.id}`);
      return;
    }
    if (to === 'gasto') {
      navigate(`/gasto/${procedure.id}`);
      return;
    }
    // 'cerrado' — transición directa sin documento.
    try {
      await transitionMutation.mutateAsync({ id: procedure.id, body: { to } });
      showToast({
        kind: 'success',
        title: `Episodio marcado como ${EPISODE_STATUS_LABELS[to]}`,
        description: `Transición desde ${EPISODE_STATUS_LABELS[procedure.estado]}.`,
      });
    } catch (err) {
      showToast({
        kind: 'danger',
        title: 'No fue posible avanzar el episodio',
        description: err instanceof Error ? err.message : 'Error inesperado.',
      });
    }
  };

  // Conteo de firmas para el banner — considera TODOS los procedimientos del
  // episodio (principal + adicionales). Un documento se requiere si cualquier
  // procedimiento lo necesita.
  const tiposDelEpisodio = useMemo<string[]>(
    () => [procedure.tipo, ...(procedure.tiposAdicionales ?? [])],
    [procedure.tipo, procedure.tiposAdicionales],
  );
  const docsRequiredHere = requiredDocsForEpisode(tiposDelEpisodio);
  const firmadosCount = docsRequiredHere.filter(
    (k) => procedure.docs[k] === 'firmado',
  ).length;
  const totalDocsRequired = docsRequiredHere.length;

  // Días desde la creación (createdAt o fecha del procedimiento como fallback).
  const daysOpen = useMemo(() => {
    const start = procedure.createdAt ?? `${procedure.fecha}T${procedure.hora}:00`;
    const startMs = new Date(start).getTime();
    if (Number.isNaN(startMs)) return null;
    const diff = Math.max(0, Math.floor((Date.now() - startMs) / 86_400_000));
    return diff;
  }, [procedure.createdAt, procedure.fecha, procedure.hora]);

  const patientAlergias =
    patientQuery.data?.alergias !== undefined && patientQuery.data.alergias.trim() !== ''
      ? patientQuery.data.alergias
      : null;
  const hasMeaningfulAlergia =
    patientAlergias !== null &&
    patientAlergias.toLowerCase() !== 'ninguna' &&
    patientAlergias.toLowerCase() !== 'ninguna reportada';

  return (
    <div className="flex flex-col gap-5 animate-fade-in-up">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/procedimientos">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </Button>
        <Button variant="secondary" size="sm" asChild>
          <Link to={`/procedimientos/${procedure.id}/imprimir`}>
            <Printer className="w-4 h-4" /> Imprimir episodio
          </Link>
        </Button>
      </div>

      {/* Info bar: alergias destacadas + progreso firmas + tiempo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div
          className={cn(
            'rounded-md border px-4 py-3 flex items-start gap-3',
            hasMeaningfulAlergia
              ? 'bg-danger-soft border-red-200'
              : 'bg-surface border-border',
          )}
        >
          <AlertTriangle
            className={cn(
              'w-5 h-5 flex-shrink-0 mt-0.5',
              hasMeaningfulAlergia ? 'text-danger' : 'text-text-muted',
            )}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">
              Alergias del paciente
            </div>
            <div
              className={cn(
                'text-sm font-semibold truncate',
                hasMeaningfulAlergia ? 'text-red-800' : 'text-text-soft',
              )}
              title={patientAlergias ?? undefined}
            >
              {hasMeaningfulAlergia
                ? patientAlergias
                : patientAlergias !== null
                  ? 'Ninguna reportada'
                  : '—'}
            </div>
          </div>
        </div>

        <div className="rounded-md border border-border bg-surface px-4 py-3 flex items-start gap-3">
          <div
            className={cn(
              'w-5 h-5 rounded-full grid place-items-center flex-shrink-0 mt-0.5 text-white text-[10px] font-extrabold',
              totalDocsRequired > 0 && firmadosCount === totalDocsRequired
                ? 'bg-success'
                : 'bg-primary',
            )}
          >
            {firmadosCount}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">
              Firmas completas
            </div>
            <div className="text-sm font-semibold text-text">
              {totalDocsRequired === 0
                ? 'Sin documentos requeridos'
                : `${firmadosCount} de ${totalDocsRequired} documento${totalDocsRequired === 1 ? '' : 's'}`}
              {totalDocsRequired > 0 && firmadosCount === totalDocsRequired && (
                <span className="text-success font-bold ml-1">· ✓ Completo</span>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-md border border-border bg-surface px-4 py-3 flex items-start gap-3">
          <Clock className="w-5 h-5 text-text-muted flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">
              Tiempo desde apertura
            </div>
            <div className="text-sm font-semibold text-text">
              {daysOpen === null
                ? '—'
                : daysOpen === 0
                  ? 'Hoy'
                  : daysOpen === 1
                    ? '1 día'
                    : `${daysOpen} días`}
            </div>
          </div>
        </div>
      </div>

      {/* Header card con stepper visual del progreso del episodio */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-extrabold text-text">Episodio {procedure.id}</h2>
              <Badge variant={variant}>
                <BadgeDot /> {stateLabel}
              </Badge>
            </div>
            <div className="text-text-muted text-sm mt-1">
              {procedure.tipo}
              {procedure.tiposAdicionales !== undefined &&
                procedure.tiposAdicionales.length > 0 && (
                  <>
                    {' + '}
                    <Badge variant="indigo" title={procedure.tiposAdicionales.join(' · ')}>
                      {procedure.tiposAdicionales.length} adicional
                      {procedure.tiposAdicionales.length === 1 ? '' : 'es'}
                    </Badge>
                  </>
                )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <CalendarIcon className="w-4 h-4" />
            {formatShortDateEs(procedure.fecha)} · {procedure.hora}
            {procedure.sala !== undefined && <> · {procedure.sala}</>}
          </div>
        </div>
        <EpisodeStepper
          current={procedure.estado}
          onTransition={(to) => void handleStepClick(to)}
          disabled={transitionMutation.isPending}
          skip={[
            ...(tiposDelEpisodio.some(requiresAnesthesia) ? [] : (['anestesia'] as const)),
            ...(tiposDelEpisodio.some(requiresExpense) ? [] : (['gasto'] as const)),
          ]}
        />
      </Card>

      <PatientFlowCard procedure={procedure} />

      <ResultsCard procedure={procedure} />


      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        <div className="flex flex-col gap-5">
          <PatientCard
            isLoading={patientQuery.isLoading}
            isError={patientQuery.isError}
            patient={patientQuery.data}
          />

          <ProcedureDataCard procedure={procedure} userById={userById} />

          <DocumentsCard procedure={procedure} />

          <TimelineCard procedureId={procedure.id} />
        </div>

        <div className="flex flex-col gap-5">
          <ActionsCard procedure={procedure} isTerminal={isTerminal} allowedNext={allowedNext} />

          <Card className="p-5">
            <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
              <CardTitle>Editar datos básicos</CardTitle>
              <CardDescription>
                Fecha, hora, sala, profesionales y observaciones.
              </CardDescription>
            </CardHeader>
            <Button variant="secondary" className="w-full" asChild>
              <Link to={`/procedimientos/${procedure.id}/editar`}>
                <Pencil className="w-4 h-4" /> Editar episodio
              </Link>
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Patient card                                                               */
/* -------------------------------------------------------------------------- */

interface PatientCardProps {
  isLoading: boolean;
  isError: boolean;
  patient: ReturnType<typeof usePatientById>['data'];
}

function PatientCard({ isLoading, isError, patient }: PatientCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Datos del paciente</CardTitle>
          <CardDescription>Información demográfica y clínica básica.</CardDescription>
        </div>
      </CardHeader>
      <div className="p-6">
        {isLoading ? (
          <div className="space-y-3" aria-busy="true">
            <div className="h-6 w-1/3 rounded bg-surface-alt animate-pulse" />
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 rounded bg-surface-alt animate-pulse" />
              ))}
            </div>
          </div>
        ) : isError || patient === undefined ? (
          <div className="text-sm text-text-muted">
            No fue posible cargar los datos del paciente.
          </div>
        ) : (
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'w-12 h-12 rounded-md grid place-items-center text-white font-bold flex-shrink-0',
                avatarGradient(patient.id),
              )}
            >
              {initials(patient.nombres, patient.apellidos)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/pacientes/${patient.id}`}
                  className="font-semibold text-text hover:text-primary"
                >
                  {patient.nombres} {patient.apellidos}
                </Link>
                <Badge variant="indigo">{patient.id}</Badge>
              </div>
              <div className="text-text-muted text-xs">
                {patient.tipoDoc} {patient.documento} · {ageFromBirthdate(patient.fechaNacimiento)}{' '}
                años ·{' '}
                {patient.sexo === 'F'
                  ? 'Femenino'
                  : patient.sexo === 'M'
                    ? 'Masculino'
                    : 'Otro'}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4 text-sm">
                <Row icon={<IdCard className="w-4 h-4" />} label="Entidad">
                  {patient.eps ?? '—'}
                </Row>
              </div>
              {patient.alergias !== undefined && patient.alergias !== '' && (
                <div className="mt-3">
                  <Badge variant="red">
                    <AlertTriangle className="w-3.5 h-3.5" /> Alergias: {patient.alergias}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Procedure data card                                                        */
/* -------------------------------------------------------------------------- */

interface ProcedureDataCardProps {
  procedure: Procedure;
  userById: Map<string, string>;
}

function ProcedureDataCard({ procedure, userById }: ProcedureDataCardProps): JSX.Element {
  const medico = userById.get(procedure.medicoId) ?? procedure.medicoId;
  const anestesiologo =
    procedure.anestesiologoId !== undefined
      ? (userById.get(procedure.anestesiologoId) ?? procedure.anestesiologoId)
      : null;

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Datos del procedimiento</CardTitle>
          <CardDescription>Programación y equipo asignado.</CardDescription>
        </div>
      </CardHeader>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <Row icon={<CalendarIcon className="w-4 h-4" />} label="Fecha">
          {formatShortDateEs(procedure.fecha)}
        </Row>
        <Row icon={<Clock className="w-4 h-4" />} label="Hora">
          {procedure.hora}
        </Row>
        <Row icon={<FileText className="w-4 h-4" />} label="Entidad">
          {procedure.entidad ?? '—'}
        </Row>
        <Row icon={<Stethoscope className="w-4 h-4" />} label="Médico tratante">
          {medico}
        </Row>
        <Row icon={<UserCircle2 className="w-4 h-4" />} label="Anestesiólogo">
          {anestesiologo ?? '—'}
        </Row>
        <Row icon={<FileText className="w-4 h-4" />} label="Tipo">
          <div className="flex flex-col gap-0.5">
            <span>{procedure.tipo}</span>
            {procedure.tiposAdicionales !== undefined &&
              procedure.tiposAdicionales.map((t) => (
                <span key={t} className="text-text-muted text-[12px]">
                  + {t}
                </span>
              ))}
          </div>
        </Row>
        <div className="md:col-span-2">
          <Row icon={<ClipboardList className="w-4 h-4" />} label="Observaciones">
            {procedure.observaciones ?? '—'}
          </Row>
        </div>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Documents card                                                             */
/* -------------------------------------------------------------------------- */

interface DocumentsCardProps {
  procedure: Procedure;
}

const DOC_VARIANT: Record<'firmado' | 'pendiente' | 'borrador', 'green' | 'gray' | 'amber'> = {
  firmado: 'green',
  pendiente: 'gray',
  borrador: 'amber',
};

const DOC_LABEL: Record<'firmado' | 'pendiente' | 'borrador', string> = {
  firmado: 'Firmado',
  pendiente: 'Pendiente',
  borrador: 'Borrador',
};

type DocKey = 'consentimiento' | 'anestesia' | 'gasto';

function DocumentsCard({ procedure }: DocumentsCardProps): JSX.Element {
  const [expanded, setExpanded] = useState<DocKey | null>(null);

  // Considera TODOS los procedimientos del episodio (principal + adicionales)
  // — un documento es requerido si cualquiera lo necesita.
  const tiposEpisodio = [procedure.tipo, ...(procedure.tiposAdicionales ?? [])];
  const required = requiredDocsForEpisode(tiposEpisodio);
  const firmadosCount = required.filter((k) => procedure.docs[k] === 'firmado').length;
  const totalRequired = required.length;

  const allDocs: Array<{
    key: DocKey;
    title: string;
    description: string;
    icon: JSX.Element;
  }> = [
    {
      key: 'consentimiento',
      title: 'Consentimiento informado',
      description: 'Firma del paciente y del médico tratante.',
      icon: <ClipboardList className="w-4 h-4" />,
    },
    {
      key: 'anestesia',
      title: 'Registro de anestesia',
      description: 'Valoración ASA, medicamentos y signos vitales.',
      icon: <Activity className="w-4 h-4" />,
    },
    {
      key: 'gasto',
      title: 'Hoja de gasto',
      description: 'Insumos y medicamentos consumidos durante el episodio.',
      icon: <Pill className="w-4 h-4" />,
    },
  ];
  const docs = allDocs.filter((d) => required.includes(d.key));

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Documentos clínicos</CardTitle>
          <CardDescription>
            {totalRequired === 0
              ? 'Este procedimiento no requiere documentos firmados.'
              : `${firmadosCount} de ${totalRequired} firmado${totalRequired === 1 ? '' : 's'} — click en cualquier documento firmado para previsualizar.`}
          </CardDescription>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <CheckCircle2
            className={cn(
              'w-4 h-4',
              totalRequired > 0 && firmadosCount === totalRequired
                ? 'text-success'
                : 'text-text-muted',
            )}
          />
          <span className="font-bold tabular-nums">
            {firmadosCount}/{totalRequired}
          </span>
        </div>
      </CardHeader>
      <div className="px-4 pb-4 flex flex-col gap-2">
        {docs.map((d) => {
          const status = procedure.docs[d.key];
          const isExpanded = expanded === d.key;
          const isFirmado = status === 'firmado';
          return (
            <div
              key={d.key}
              className={cn(
                'rounded-md border bg-surface transition-colors',
                isFirmado ? 'border-success/40' : 'border-border',
                isExpanded && 'shadow-sm',
              )}
            >
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : isFirmado ? d.key : null)}
                disabled={!isFirmado}
                className={cn(
                  'w-full p-3 flex items-center gap-3 text-left',
                  isFirmado && 'hover:bg-surface-alt cursor-pointer',
                  !isFirmado && 'cursor-default',
                )}
                aria-expanded={isExpanded}
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-md grid place-items-center flex-shrink-0',
                    isFirmado ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-muted',
                  )}
                >
                  {d.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-text">{d.title}</div>
                  <div className="text-xs text-text-muted truncate">{d.description}</div>
                </div>
                <Badge variant={DOC_VARIANT[status]}>{DOC_LABEL[status]}</Badge>
                <div onClick={(e) => e.stopPropagation()}>
                  <DocumentAction docKey={d.key} procedure={procedure} />
                </div>
                {isFirmado && (
                  <span className="text-text-muted">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </span>
                )}
              </button>
              {isExpanded && isFirmado && (
                <div className="border-t border-border p-4 bg-surface-alt animate-fade-in-up">
                  <DocumentPreview docKey={d.key} procedure={procedure} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Previews inline de cada documento                                          */
/* -------------------------------------------------------------------------- */

interface DocumentPreviewProps {
  docKey: DocKey;
  procedure: Procedure;
}

function DocumentPreview({ docKey, procedure }: DocumentPreviewProps): JSX.Element {
  if (docKey === 'consentimiento') {
    return <ConsentPreview procedureId={procedure.id} />;
  }
  if (docKey === 'anestesia') {
    return <AnesthesiaPreview procedureId={procedure.id} />;
  }
  return <ExpensePreview procedureId={procedure.id} />;
}

function ConsentPreview({ procedureId }: { procedureId: ProcedureId }): JSX.Element {
  const { data, isLoading, isError } = useConsentByProcedure(procedureId);
  if (isLoading) return <PreviewSkeleton />;
  if (isError || data === null || data === undefined) {
    return <PreviewError message="No fue posible cargar el consentimiento." />;
  }
  return (
    <div className="flex flex-col gap-3">
      <PreviewMeta
        items={[
          ['Firmado el', data.firmadoEn ? new Date(data.firmadoEn).toLocaleString('es-CO') : '—'],
          ['Médico', data.firmaMedicoId],
        ]}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <ConsentCheck label="Riesgos explicados" checked={data.riesgosExplicados} />
        <ConsentCheck label="Alternativas explicadas" checked={data.alternativasExplicadas} />
        <ConsentCheck label="Preguntas resueltas" checked={data.preguntasResueltas} />
      </div>
      {data.observaciones !== undefined && data.observaciones.trim() !== '' && (
        <PreviewObservations text={data.observaciones} />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.firmaPaciente !== undefined && (
          <SignatureBox label="Firma paciente" dataUrl={data.firmaPaciente} />
        )}
        {data.firmaAcompanante !== undefined && (
          <SignatureBox
            label={`Firma acompañante${
              data.nombreAcompanante !== undefined ? ` — ${data.nombreAcompanante}` : ''
            }${
              data.parentescoAcompanante !== undefined ? ` (${data.parentescoAcompanante})` : ''
            }`}
            dataUrl={data.firmaAcompanante}
          />
        )}
        <SignatureBox
          label="Firma médico"
          dataUrl={data.firmaMedico}
          authUrl={`/api/users/${encodeURIComponent(data.firmaMedicoId)}/firma`}
        />
      </div>
    </div>
  );
}

function AnesthesiaPreview({ procedureId }: { procedureId: ProcedureId }): JSX.Element {
  const { data, isLoading, isError } = useAnesthesiaByProcedure(procedureId);
  if (isLoading) return <PreviewSkeleton />;
  if (isError || data === null || data === undefined) {
    return <PreviewError message="No fue posible cargar el registro de anestesia." />;
  }
  const v = data.preAnestesia?.vitalesIniciales;
  return (
    <div className="flex flex-col gap-3">
      <PreviewMeta
        items={[
          ['Firmado el', data.firmadoEn ? new Date(data.firmadoEn).toLocaleString('es-CO') : '—'],
          ['Anestesiólogo', data.anestesiologoId],
          ['Técnica', data.tecnica ?? '—'],
          ['Horario', `${data.inicioHora ?? '—'} – ${data.finHora ?? '—'}`],
        ]}
      />
      {data.preAnestesia !== undefined && v !== undefined && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <PreviewStat label="Peso · Talla" value={`${data.preAnestesia.pesoKg} kg · ${data.preAnestesia.tallaCm} cm`} />
          <PreviewStat label="ASA / Mallampati" value={`ASA ${data.preAnestesia.asa} · M ${data.preAnestesia.mallampati ?? '—'}`} />
          <PreviewStat label="Ayuno" value={`${data.preAnestesia.ayunoHoras} h`} />
          <PreviewStat label="TA inicial" value={`${v.tensionSistolica}/${v.tensionDiastolica}`} />
          <PreviewStat label="FC" value={`${v.frecuenciaCardiaca} lpm`} />
          <PreviewStat label="SatO₂" value={`${v.saturacionO2}%`} />
          {v.frecuenciaRespiratoria !== undefined && (
            <PreviewStat label="FR" value={`${v.frecuenciaRespiratoria} rpm`} />
          )}
          {v.temperatura !== undefined && (
            <PreviewStat label="T°" value={`${v.temperatura} °C`} />
          )}
        </div>
      )}
      {data.medicamentos !== undefined && data.medicamentos.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold flex items-center gap-1.5">
              <Pill className="w-3 h-3" /> Medicamentos administrados
            </div>
            <span className="text-[10px] font-bold text-primary bg-primary-soft px-2 py-0.5 rounded-full">
              {data.medicamentos.length} ítem{data.medicamentos.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="divide-y divide-border rounded-md border border-border bg-surface overflow-hidden">
            {data.medicamentos.map((m, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-surface-alt transition-colors"
              >
                <code className="font-mono text-[10px] text-text-muted bg-surface-alt px-1.5 py-0.5 rounded flex-shrink-0">
                  {m.codigo}
                </code>
                <div className="flex-1 min-w-0 font-medium text-text truncate">{m.nombre}</div>
                <span className="font-bold text-text tabular-nums flex-shrink-0">{m.dosis}</span>
                <span className="inline-flex items-center justify-center min-w-[32px] h-5 px-1.5 rounded bg-primary-soft text-primary text-[10px] font-bold uppercase flex-shrink-0">
                  {m.via}
                </span>
                <span className="text-text-muted font-mono text-[11px] tabular-nums flex-shrink-0 w-[42px] text-right">
                  {m.hora}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.complicaciones !== undefined && data.complicaciones.trim() !== '' && (
        <PreviewObservations text={data.complicaciones} label="Complicaciones" tone="warning" />
      )}
      {data.observaciones !== undefined && data.observaciones.trim() !== '' && (
        <PreviewObservations text={data.observaciones} />
      )}
      <SignatureBox label="Firma anestesiólogo" dataUrl={data.firmaAnestesiologo ?? undefined} />
    </div>
  );
}

function ExpensePreview({ procedureId }: { procedureId: ProcedureId }): JSX.Element {
  const { data, isLoading, isError } = useExpenseByProcedure(procedureId);
  if (isLoading) return <PreviewSkeleton />;
  if (isError || data === null || data === undefined) {
    return <PreviewError message="No fue posible cargar la hoja de gasto." />;
  }
  const insumos = data.lineas.filter((l) => l.tipo === 'insumo');
  const medicamentos = data.lineas.filter((l) => l.tipo === 'medicamento');
  const totalUnidades = data.lineas.reduce((acc, l) => acc + l.cantidad, 0);
  return (
    <div className="flex flex-col gap-3">
      <PreviewMeta
        items={[
          ['Firmado el', data.firmadoEn ? new Date(data.firmadoEn).toLocaleString('es-CO') : '—'],
          ['Enfermera', data.enfermeraId],
          ['Líneas', `${data.lineas.length} (${insumos.length} insumos · ${medicamentos.length} medicamentos)`],
          ['Unidades totales', String(totalUnidades)],
        ]}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <ExpenseList
          title="Insumos"
          items={insumos}
          icon={<ClipboardList className="w-3 h-3" />}
          color="blue"
        />
        <ExpenseList
          title="Medicamentos"
          items={medicamentos}
          icon={<Pill className="w-3 h-3" />}
          color="purple"
        />
      </div>
      {data.observaciones !== undefined && data.observaciones.trim() !== '' && (
        <PreviewObservations text={data.observaciones} />
      )}
      <SignatureBox label="Firma enfermera" dataUrl={data.firmaEnfermera ?? undefined} />
    </div>
  );
}

function ExpenseList({
  title,
  items,
  icon,
  color,
}: {
  title: string;
  items: ReadonlyArray<{
    codigo: string;
    nombre: string;
    cantidad: number;
    unidad?: string;
  }>;
  icon?: JSX.Element;
  color?: 'blue' | 'purple';
}): JSX.Element {
  const accent = color === 'purple' ? 'text-purple-600 bg-purple-50' : 'text-blue-600 bg-blue-50';
  const total = items.reduce((acc, it) => acc + it.cantidad, 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1.5">
          {icon}
          {title}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-text-muted">{items.length} ítem{items.length === 1 ? '' : 's'}</span>
          <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full', accent)}>
            {total} u.
          </span>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-text-muted text-xs italic px-2 py-3 border border-dashed border-border rounded">
          Sin registros.
        </div>
      ) : (
        <div className="divide-y divide-border rounded-md border border-border bg-surface overflow-hidden max-h-[180px] overflow-y-auto">
          {items.map((l, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-surface-alt transition-colors"
            >
              <code className="bg-surface-alt px-1.5 py-0.5 rounded text-[10px] text-text-muted flex-shrink-0">
                {l.codigo}
              </code>
              <span className="flex-1 truncate text-text">{l.nombre}</span>
              <span className="text-text font-bold tabular-nums flex-shrink-0">{l.cantidad}</span>
              <span className="text-text-muted text-[10px] w-[34px] flex-shrink-0">{l.unidad ?? ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConsentCheck({ label, checked }: { label: string; checked: boolean }): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md border px-2 py-1.5',
        checked ? 'bg-success-soft border-success/30 text-green-900' : 'bg-surface border-border text-text-muted',
      )}
    >
      <CheckCircle2 className={cn('w-3.5 h-3.5', checked ? 'text-success' : 'text-text-muted opacity-50')} />
      <span>{label}</span>
    </div>
  );
}

function PreviewSkeleton(): JSX.Element {
  return (
    <div className="space-y-2" aria-busy="true">
      <div className="h-4 w-1/3 rounded bg-surface-alt animate-pulse" />
      <div className="h-16 rounded bg-surface-alt animate-pulse" />
      <div className="h-16 rounded bg-surface-alt animate-pulse" />
    </div>
  );
}

function PreviewError({ message }: { message: string }): JSX.Element {
  return <div className="text-sm text-red-700 bg-danger-soft border border-red-200 rounded px-3 py-2">{message}</div>;
}

function PreviewMeta({ items }: { items: ReadonlyArray<[string, string]> }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {items.map(([k, v]) => (
        <div key={k} className="flex gap-1">
          <span className="text-text-muted">{k}:</span>
          <span className="text-text font-medium">{v}</span>
        </div>
      ))}
    </div>
  );
}

function PreviewStat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="bg-surface border border-border rounded-md px-2.5 py-2 hover:border-primary/30 hover:shadow-sm transition-all">
      <div className="text-[9px] uppercase tracking-wider text-text-muted font-bold">{label}</div>
      <div className="text-sm font-bold text-text tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function PreviewObservations({
  text,
  label = 'Observaciones',
  tone = 'neutral',
}: {
  text: string;
  label?: string;
  tone?: 'neutral' | 'warning';
}): JSX.Element {
  const isWarning = tone === 'warning';
  return (
    <div className="text-xs">
      <div
        className={cn(
          'text-[10px] uppercase tracking-wider font-bold mb-0.5 flex items-center gap-1',
          isWarning ? 'text-warning' : 'text-text-muted',
        )}
      >
        {isWarning && <AlertTriangle className="w-3 h-3" />}
        {label}
      </div>
      <div
        className={cn(
          'rounded-md px-2.5 py-2 border leading-relaxed',
          isWarning
            ? 'bg-warning-soft border-amber-200 text-amber-900'
            : 'bg-surface border-border text-text-soft',
        )}
      >
        {text}
      </div>
    </div>
  );
}

/**
 * Caja de firma. `dataUrl` es la firma capturada (data URL). Cuando viene vacía
 * —caso normal del médico, que tiene su firma pre-cargada en el perfil y no
 * firma en tablet— usamos `authUrl` para traerla del endpoint protegido; un
 * <img src=""> mostraba un ícono roto.
 */
function SignatureBox({
  label,
  dataUrl,
  authUrl,
}: {
  label: string;
  dataUrl?: string | undefined;
  authUrl?: string | undefined;
}): JSX.Element {
  const sinFirma = <span className="text-[11px] text-text-muted">Sin firma</span>;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
        {label}
      </div>
      <div className="bg-white border border-border rounded p-2 inline-block">
        {dataUrl !== undefined && dataUrl !== '' ? (
          <img src={dataUrl} alt={label} className="max-h-[80px] w-auto" />
        ) : authUrl !== undefined ? (
          <AuthenticatedImage
            url={authUrl}
            alt={label}
            className="max-h-[80px] w-auto"
            fallback={sinFirma}
          />
        ) : (
          sinFirma
        )}
      </div>
    </div>
  );
}

interface DocumentActionProps {
  docKey: 'consentimiento' | 'anestesia' | 'gasto';
  procedure: Procedure;
}

function DocumentAction({ docKey, procedure }: DocumentActionProps): JSX.Element {
  const isTerminal = procedure.estado === 'cerrado' || procedure.estado === 'anulado';

  if (docKey === 'gasto') {
    const gastoStatus = procedure.docs.gasto;
    // La hoja de gasto se habilita con el consentimiento firmado — NO depende de
    // la anestesia (se diligencian en paralelo).
    const consentFirmadoGasto = procedure.docs.consentimiento === 'firmado';
    const canSignGasto = consentFirmadoGasto && !isTerminal;

    if (gastoStatus === 'firmado') {
      return (
        <Button variant="secondary" size="sm" asChild>
          <Link to={`/gasto/${procedure.id}`}>Ver hoja de gasto</Link>
        </Button>
      );
    }
    if (canSignGasto) {
      return (
        <Button variant="primary" size="sm" asChild>
          <Link to={`/gasto/${procedure.id}`}>Registrar gasto</Link>
        </Button>
      );
    }
    if (isTerminal) {
      return (
        <Button variant="secondary" size="sm" disabled>
          Sin hoja
        </Button>
      );
    }
    if (!consentFirmadoGasto) {
      return (
        <Button variant="secondary" size="sm" disabled>
          Pendiente consentimiento
        </Button>
      );
    }
    return (
      <Button variant="secondary" size="sm" asChild>
        <Link to={`/gasto/${procedure.id}`}>Ver hoja de gasto</Link>
      </Button>
    );
  }

  if (docKey === 'consentimiento') {
    const status = procedure.docs.consentimiento;
    const isOpen = procedure.estado === 'abierto';

    if (status === 'firmado') {
      return (
        <Button variant="secondary" size="sm" asChild>
          <Link to={`/consentimiento/${procedure.id}`}>Ver consentimiento</Link>
        </Button>
      );
    }
    if (isOpen) {
      return (
        <Button variant="primary" size="sm" asChild>
          <Link to={`/consentimiento/${procedure.id}`}>Firmar consentimiento</Link>
        </Button>
      );
    }
    if (isTerminal) {
      return (
        <Button variant="secondary" size="sm" disabled>
          Sin consentimiento
        </Button>
      );
    }
    return (
      <Button variant="secondary" size="sm" asChild>
        <Link to={`/consentimiento/${procedure.id}`}>Ver consentimiento</Link>
      </Button>
    );
  }

  // docKey === 'anestesia'
  const anestStatus = procedure.docs.anestesia;
  const consentFirmado = procedure.docs.consentimiento === 'firmado';
  // Se habilita con el consentimiento firmado — independiente de la hoja de
  // gasto (paralelas). No depende del estado lineal del episodio.
  const canSign = consentFirmado && !isTerminal;

  if (anestStatus === 'firmado') {
    return (
      <Button variant="secondary" size="sm" asChild>
        <Link to={`/anestesia/${procedure.id}`}>Ver anestesia</Link>
      </Button>
    );
  }
  if (canSign) {
    return (
      <Button variant="primary" size="sm" asChild>
        <Link to={`/anestesia/${procedure.id}`}>Registrar anestesia</Link>
      </Button>
    );
  }
  if (isTerminal) {
    return (
      <Button variant="secondary" size="sm" disabled>
        Sin registro
      </Button>
    );
  }
  if (!consentFirmado) {
    return (
      <Button variant="secondary" size="sm" disabled>
        Pendiente consentimiento
      </Button>
    );
  }
  // Estados posteriores ya pasaron sin firma — solo lectura.
  return (
    <Button variant="secondary" size="sm" asChild>
      <Link to={`/anestesia/${procedure.id}`}>Ver anestesia</Link>
    </Button>
  );
}

/* -------------------------------------------------------------------------- */
/* Timeline / audit                                                           */
/* -------------------------------------------------------------------------- */

interface TimelineCardProps {
  procedureId: ProcedureId;
}

const MODULE_COLOR: Record<string, { bg: string; ring: string; icon: JSX.Element }> = {
  Sistema: {
    bg: 'bg-slate-400',
    ring: 'ring-slate-200',
    icon: <Clock className="w-3 h-3 text-white" />,
  },
  Pacientes: {
    bg: 'bg-blue-500',
    ring: 'ring-blue-100',
    icon: <UserCircle2 className="w-3 h-3 text-white" />,
  },
  Procedimientos: {
    bg: 'bg-teal-500',
    ring: 'ring-teal-100',
    icon: <Stethoscope className="w-3 h-3 text-white" />,
  },
  Consentimiento: {
    bg: 'bg-blue-600',
    ring: 'ring-blue-100',
    icon: <ClipboardList className="w-3 h-3 text-white" />,
  },
  Anestesia: {
    bg: 'bg-purple-500',
    ring: 'ring-purple-100',
    icon: <Activity className="w-3 h-3 text-white" />,
  },
  'Hoja de gasto': {
    bg: 'bg-amber-500',
    ring: 'ring-amber-100',
    icon: <Pill className="w-3 h-3 text-white" />,
  },
};

function TimelineCard({ procedureId }: TimelineCardProps): JSX.Element {
  const { data, isLoading, isError } = useAuditList({
    page: 1,
    pageSize: 50,
    entidad: procedureId,
  });

  // Cronológico ascendente para leer como historia.
  const events = useMemo(() => {
    const items = data?.items ?? [];
    return [...items].sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Línea de tiempo</CardTitle>
          <CardDescription>
            Auditoría inmutable · {events.length} evento{events.length === 1 ? '' : 's'} con
            hash-chain verificable.
          </CardDescription>
        </div>
      </CardHeader>
      <div className="p-6">
        {isLoading ? (
          <div className="space-y-2" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 rounded bg-surface-alt animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <div className="text-sm text-text-muted">
            No fue posible cargar la bitácora de auditoría.
          </div>
        ) : events.length === 0 ? (
          <div className="text-sm text-text-muted flex items-center gap-2">
            <HistoryIcon className="w-4 h-4" /> Sin eventos registrados para este episodio.
          </div>
        ) : (
          <ol className="relative space-y-4">
            {/* Línea vertical conectando todos los dots */}
            <div
              aria-hidden
              className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-border"
            />
            {events.map((e, idx) => {
              const palette = MODULE_COLOR[e.modulo] ?? MODULE_COLOR.Sistema!;
              const isLast = idx === events.length - 1;
              return (
                <li
                  key={e.id}
                  className="relative pl-10 animate-fade-in-up"
                >
                  {/* Dot */}
                  <div
                    className={cn(
                      'absolute left-0 top-0.5 w-8 h-8 rounded-full grid place-items-center ring-4 z-10',
                      palette.bg,
                      palette.ring,
                    )}
                  >
                    {palette.icon}
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-text">{e.accion}</span>
                        <Badge variant="gray">{e.modulo}</Badge>
                      </div>
                      <div className="text-xs text-text-muted mt-0.5">
                        por <span className="font-medium text-text-soft">{e.usuarioNombre}</span>
                        {e.ip !== undefined && (
                          <>
                            {' '}· IP <code className="text-[10px]">{e.ip}</code>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-text-muted whitespace-nowrap text-right">
                      <div>{new Date(e.fecha).toLocaleDateString('es-CO')}</div>
                      <div className="font-mono">
                        {new Date(e.fecha).toLocaleTimeString('es-CO', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>
                  {/* Hash chain footer del evento (solo para el último — los demás se intuyen) */}
                  {isLast && (
                    <div className="mt-2 text-[10px] text-text-muted font-mono opacity-70">
                      hash: {e.hash.slice(0, 16)}…{e.hash.slice(-8)}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Actions card (transitions / cancel)                                        */
/* -------------------------------------------------------------------------- */

interface ActionsCardProps {
  procedure: Procedure;
  isTerminal: boolean;
  allowedNext: readonly EpisodeStatus[];
}

function ActionsCard({ procedure, isTerminal, allowedNext }: ActionsCardProps): JSX.Element {
  const variant = EPISODE_STATUS_VARIANTS[procedure.estado];
  const stateLabel = EPISODE_STATUS_LABELS[procedure.estado];
  const navigate = useNavigate();
  const { showToast } = useToast();
  const permisos = useAuthStore((s) => s.user?.permisos ?? []);
  const canDelete = hasPermission(permisos, 'procedures:delete');
  const deleteMutation = useDeleteProcedure();

  const [pendingTransition, setPendingTransition] = useState<EpisodeStatus | null>(null);
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const handleDelete = async (): Promise<void> => {
    try {
      await deleteMutation.mutateAsync({ id: procedure.id });
      showToast({
        kind: 'success',
        title: 'Procedimiento eliminado',
        description: `El episodio ${procedure.id} y sus documentos se eliminaron definitivamente.`,
      });
      setDeleteOpen(false);
      navigate('/procedimientos');
    } catch (err) {
      showToast({
        kind: 'danger',
        title: 'No se pudo eliminar',
        description: err instanceof ApiClientError ? err.message : 'Error desconocido',
      });
    }
  };

  return (
    <Card className="p-5">
      <CardHeader className="px-0 pt-0 pb-3 mb-3 border-b border-border">
        <CardTitle>Acciones</CardTitle>
        <CardDescription>
          Avanza el estado del episodio según el flujo clínico.
        </CardDescription>
      </CardHeader>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-text-muted uppercase tracking-wider font-semibold">
          Estado actual
        </span>
        <Badge variant={variant} className="text-sm px-3 py-1">
          <BadgeDot /> {stateLabel}
        </Badge>
      </div>

      {isTerminal ? (
        <div className="text-sm text-text-muted bg-surface-alt rounded-md p-3 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <span>
              Episodio {procedure.estado === 'cerrado' ? 'cerrado' : 'anulado'} — no se aceptan
              más cambios.
            </span>
            {procedure.estado === 'anulado' &&
              procedure.motivoCancelacion !== undefined &&
              procedure.motivoCancelacion !== '' && (
                <div className="mt-1 text-text">
                  <span className="font-semibold">Motivo de anulación:</span>{' '}
                  {procedure.motivoCancelacion}
                </div>
              )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {allowedNext.length === 0 ? (
              <div className="text-sm text-text-muted">
                No hay transiciones disponibles desde este estado.
              </div>
            ) : (
              allowedNext.map((to) => (
                <Button
                  key={to}
                  variant="secondary"
                  className="justify-start"
                  onClick={() => setPendingTransition(to)}
                >
                  <CheckCircle2 className="w-4 h-4" /> Marcar como {EPISODE_STATUS_LABELS[to]}
                </Button>
              ))
            )}
          </div>
          <div className="my-4 border-t border-border" />
          <Button
            variant="secondary"
            className="w-full !bg-danger-soft !text-red-800 hover:!bg-red-100 border-red-200"
            onClick={() => setCancelOpen(true)}
          >
            <Ban className="w-4 h-4" /> Anular episodio
          </Button>
        </>
      )}

      {canDelete && (
        <>
          <div className="my-4 border-t border-border" />
          <Button
            variant="secondary"
            className="w-full !bg-danger-soft !text-red-800 hover:!bg-red-100 border-red-200"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="w-4 h-4" /> Eliminar procedimiento
          </Button>
          <p className="text-[11px] text-text-muted mt-1.5">
            Borra el episodio y sus documentos de forma definitiva. Deja de contar en el conteo de
            la enfermera de ese día.
          </p>
        </>
      )}

      {pendingTransition !== null && (
        <TransitionDialog
          procedureId={procedure.id}
          to={pendingTransition}
          onClose={() => setPendingTransition(null)}
        />
      )}
      {cancelOpen && (
        <CancelDialog procedureId={procedure.id} onClose={() => setCancelOpen(false)} />
      )}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        danger
        title="Eliminar procedimiento definitivamente"
        confirmLabel="Sí, eliminar"
        cancelLabel="No, conservar"
        loading={deleteMutation.isPending}
        onConfirm={() => void handleDelete()}
        description={
          <>
            Vas a eliminar el episodio <strong>{procedure.id}</strong> y todos sus documentos
            (consentimiento, anestesia, hoja de gasto y resultados). Esta acción{' '}
            <strong>no se puede deshacer</strong> y el episodio dejará de contar para la enfermera
            de ese día.
          </>
        }
      />
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Transition / cancel dialogs                                                */
/* -------------------------------------------------------------------------- */

interface TransitionDialogProps {
  procedureId: ProcedureId;
  to: EpisodeStatus;
  onClose(): void;
}

function TransitionDialog({ procedureId, to, onClose }: TransitionDialogProps): JSX.Element {
  const transition = useTransitionProcedure();
  const [observaciones, setObservaciones] = useState<string>('');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setErrMsg(null);
    try {
      const body =
        observaciones.trim() === ''
          ? { to }
          : { to, observaciones: observaciones.trim() };
      await transition.mutateAsync({ id: procedureId, body });
      onClose();
    } catch (err) {
      setErrMsg(buildMutationError(err));
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmar transición</DialogTitle>
          <DialogDescription>
            Vas a marcar el episodio como <strong>{EPISODE_STATUS_LABELS[to]}</strong>. Esta
            acción quedará registrada en la bitácora.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="trans-obs">Observaciones (opcional)</Label>
          <textarea
            id="trans-obs"
            rows={3}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Notas clínicas relevantes para la transición…"
          />
        </div>

        {errMsg !== null && (
          <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200">
            {errMsg}
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={transition.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={transition.isPending}>
            {transition.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Aplicando…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" /> Confirmar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CancelDialogProps {
  procedureId: ProcedureId;
  onClose(): void;
}

function CancelDialog({ procedureId, onClose }: CancelDialogProps): JSX.Element {
  const cancel = useCancelProcedure();
  const [motivo, setMotivo] = useState<string>('');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const trimmed = motivo.trim();
  const isValid = trimmed.length >= 3 && trimmed.length <= 500;

  const submit = async (): Promise<void> => {
    setErrMsg(null);
    if (!isValid) {
      setErrMsg('El motivo debe tener entre 3 y 500 caracteres.');
      return;
    }
    try {
      await cancel.mutateAsync({ id: procedureId, body: { motivo: trimmed } });
      onClose();
    } catch (err) {
      setErrMsg(buildMutationError(err));
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Anular episodio</DialogTitle>
          <DialogDescription>
            Esta acción es irreversible. El episodio quedará marcado como anulado y no podrá
            recibir más cambios.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cancel-motivo">
            Motivo de anulación <span className="text-danger">*</span>
          </Label>
          <textarea
            id="cancel-motivo"
            rows={4}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Razón clínica o administrativa…"
            maxLength={500}
          />
          <div className="text-[11px] text-text-muted">{trimmed.length}/500 caracteres</div>
        </div>

        {errMsg !== null && (
          <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200">
            {errMsg}
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={cancel.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={cancel.isPending || !isValid}
            className="!bg-danger hover:!bg-red-700"
          >
            {cancel.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Anulando…
              </>
            ) : (
              <>
                <Ban className="w-4 h-4" /> Anular episodio
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function buildMutationError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 409) {
      return err.message !== ''
        ? `Transición inválida: ${err.message}`
        : 'Transición inválida para el estado actual.';
    }
    if (err.status === 403) {
      return 'Tu rol no permite esta transición.';
    }
    if (err.status === 401) {
      return 'Sesión expirada. Vuelve a iniciar sesión.';
    }
    if (err.status === 422 || err.status === 400) {
      return 'Datos inválidos. Verifica el formulario.';
    }
    if (err.status >= 500) {
      return 'Error del servidor. Inténtalo nuevamente.';
    }
    return err.message;
  }
  return 'No fue posible procesar la solicitud.';
}

/* -------------------------------------------------------------------------- */
/* Skeleton / error                                                            */
/* -------------------------------------------------------------------------- */

function DetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <Card>
        <div className="p-6 space-y-3" aria-busy="true" aria-live="polite">
          <div className="h-6 w-1/3 rounded bg-surface-alt animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-surface-alt animate-pulse" />
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <div className="p-6 space-y-3 animate-pulse">
              <div className="h-5 w-1/4 rounded bg-surface-alt" />
              <div className="h-20 rounded bg-surface-alt" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

interface ErrorStateProps {
  title: string;
  detail: string;
  onRetry?(): void;
}

function ErrorState({ title, detail, onRetry }: ErrorStateProps): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/procedimientos">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </Button>
      <Card>
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft text-danger mb-3">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="text-text font-bold text-base mb-1">{title}</div>
          <p className="text-text-muted text-sm mb-4">{detail}</p>
          <div className="flex justify-center gap-2">
            {onRetry !== undefined && (
              <Button variant="secondary" onClick={onRetry}>
                <RefreshCw className="w-4 h-4" /> Reintentar
              </Button>
            )}
            <Button variant="secondary" asChild>
              <Link to="/procedimientos">
                <ArrowLeft className="w-4 h-4" /> Volver al listado
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Misc                                                                        */
/* -------------------------------------------------------------------------- */

interface RowProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}

function Row({ icon, label, children }: RowProps): JSX.Element {
  return (
    <div className="flex items-start gap-2.5">
      <div className="text-text-muted mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-text-muted text-[11px] uppercase tracking-wider font-semibold">
          {label}
        </div>
        <div className="text-text text-sm">{children}</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* PatientFlowCard — estado operativo del paciente (sala/canalización/fin)    */
/* -------------------------------------------------------------------------- */

function PatientFlowCard({ procedure }: { procedure: Procedure }): JSX.Element {
  const { showToast } = useToast();
  const mutation = useUpdatePatientFlow();
  const isTerminal = procedure.estado === 'cerrado' || procedure.estado === 'anulado';
  const current = procedure.estadoPaciente;

  const handleChange = (to: PatientFlowStatus): void => {
    if (to === current || mutation.isPending || isTerminal) return;
    mutation.mutate(
      { id: procedure.id, body: { estadoPaciente: to } },
      {
        onSuccess: () => {
          showToast({
            kind: 'success',
            title: 'Estado del paciente actualizado',
            description: `Ahora: ${PATIENT_FLOW_LABELS[to]}`,
          });
        },
        onError: (err) => {
          showToast({
            kind: 'danger',
            title: 'No fue posible actualizar el estado',
            description: err instanceof Error ? err.message : 'Error desconocido',
          });
        },
      },
    );
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-text-muted">
            Estado del paciente
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={PATIENT_FLOW_VARIANTS[current]}>
              <BadgeDot /> {PATIENT_FLOW_LABELS[current]}
            </Badge>
            <span className="text-[11px] text-text-muted">
              Actualizado manualmente por la enfermera
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {PATIENT_FLOW_ORDER.map((s) => {
            const active = s === current;
            return (
              <button
                key={s}
                type="button"
                onClick={() => handleChange(s)}
                disabled={active || mutation.isPending || isTerminal}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors',
                  active
                    ? 'bg-primary text-white border-primary cursor-default'
                    : 'bg-surface text-text border-border hover:border-primary hover:text-primary',
                  (mutation.isPending || isTerminal) && !active && 'opacity-50 cursor-not-allowed',
                )}
              >
                {PATIENT_FLOW_LABELS[s]}
              </button>
            );
          })}
        </div>
      </div>
      {isTerminal && (
        <div className="mt-3 text-[11px] text-text-muted">
          El episodio está {procedure.estado} — no se puede cambiar el estado del paciente.
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* ResultsCard — upload + lista de resultados del procedimiento               */
/* -------------------------------------------------------------------------- */

// La subida va directa al bucket (presigned), así que ya no la limita el
// bodyLimit de la API. Se mantiene un tope de cordura del lado del cliente.
const MAX_UPLOAD_BYTES = MAX_RESULT_UPLOAD_BYTES;

function ResultsCard({ procedure }: { procedure: Procedure }): JSX.Element {
  const { showToast } = useToast();
  const accessToken = useAuthStore((s) => s.accessToken);
  // Se pueden cargar resultados cuando el paciente quedó "Finalizado" o cuando
  // el episodio ya está "Cerrado" — los informes de patología/biopsia llegan
  // días después, con el episodio cerrado, y deben poder subirse igual.
  const habilitado =
    procedure.estadoPaciente === 'finalizado' || procedure.estado === 'cerrado';
  const resultsQuery = useResultsByProcedure(procedure.id);
  const uploadMutation = useUploadResultFile();
  const deleteMutation = useDeleteResult();
  const [descripcion, setDescripcion] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);

  // El endpoint de descarga exige JWT `Authorization: Bearer` (permiso
  // results:read), así que un `<a href>` directo navega sin el header y da 401.
  // Descargamos el archivo con fetch autenticado y lo abrimos como blob.
  // Abrimos la pestaña de forma síncrona (dentro del gesto del click) para que
  // el bloqueador de pop-ups no la mate; luego la apuntamos al blob.
  const handleView = async (resultId: string, nombreOriginal: string): Promise<void> => {
    const win = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const res = await fetch(resultDownloadUrl(resultId), {
        headers:
          accessToken !== null && accessToken.length > 0
            ? { Authorization: `Bearer ${accessToken}` }
            : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} — ${res.statusText}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (win !== null) {
        win.location.href = url;
      } else {
        // Pop-up bloqueado: caemos a descarga directa del blob.
        const a = document.createElement('a');
        a.href = url;
        a.download = nombreOriginal;
        a.click();
      }
      // El visor ya cargó el recurso; liberamos la URL un rato después.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      if (win !== null) win.close();
      showToast({
        kind: 'danger',
        title: 'No se pudo abrir el archivo',
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    }
  };

  const handleFiles = async (files: FileList | null): Promise<void> => {
    if (!habilitado || files === null || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_UPLOAD_BYTES) {
          showToast({
            kind: 'danger',
            title: 'Archivo demasiado grande',
            description: `${file.name} supera el límite de ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB.`,
          });
          continue;
        }
        try {
          // Sube directo al bucket con URL prefirmada (y cae al endpoint
          // clásico si el almacenamiento no lo admite, p. ej. en desarrollo).
          await uploadMutation.mutateAsync({
            procedureId: procedure.id,
            file,
            ...(descripcion.trim() !== '' ? { descripcion: descripcion.trim() } : {}),
          });
          showToast({
            kind: 'success',
            title: 'Resultado subido',
            description: file.name,
          });
        } catch (err) {
          showToast({
            kind: 'danger',
            title: 'Falló la subida',
            description: err instanceof Error ? err.message : 'Error desconocido',
          });
        }
      }
      setDescripcion('');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (resultId: string): void => {
    if (!confirm('¿Eliminar este resultado? Esta acción no se puede deshacer.')) return;
    deleteMutation.mutate(
      { resultId, procedureId: procedure.id },
      {
        onSuccess: () =>
          showToast({ kind: 'success', title: 'Resultado eliminado' }),
        onError: (err) =>
          showToast({
            kind: 'danger',
            title: 'No se pudo eliminar',
            description: err.message,
          }),
      },
    );
  };

  const items = resultsQuery.data?.items ?? [];

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-text-muted">
            Resultados del procedimiento
          </div>
          <div className="text-text text-base font-semibold mt-1">
            {items.length === 0
              ? 'Aún no se han cargado resultados'
              : `${items.length} archivo${items.length === 1 ? '' : 's'} cargado${
                  items.length === 1 ? '' : 's'
                }`}
          </div>
          <div className="text-[12px] text-text-muted mt-1">
            {habilitado
              ? 'Sube los informes (PDF) y fotografías (PNG/JPG/WebP). El paciente los verá en su portal.'
              : 'Disponible cuando el estado del paciente sea "Finalizado" o el episodio esté "Cerrado".'}
          </div>
        </div>
      </div>

      {habilitado && (
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="result-desc">Descripción (opcional)</Label>
            <input
              id="result-desc"
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              maxLength={500}
              placeholder="Ej. Informe de biopsia, fotos endoscopia distal…"
              className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <label
            className={cn(
              'flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed border-border bg-surface-alt text-center cursor-pointer hover:border-primary hover:bg-primary-soft transition-colors',
              uploading && 'opacity-50 cursor-wait',
            )}
          >
            <input
              type="file"
              multiple
              accept="application/pdf,image/png,image/jpeg,image/webp"
              disabled={uploading}
              onChange={(e) => void handleFiles(e.target.files)}
              className="hidden"
            />
            <div className="text-sm font-semibold text-text">
              {uploading ? 'Subiendo…' : 'Selecciona archivos para subir'}
            </div>
            <div className="text-[11px] text-text-muted">
              PDF / PNG / JPG / WebP · máx 24 MB por archivo
            </div>
          </label>
        </div>
      )}

      {items.length > 0 && (
        <div className="flex flex-col gap-2">
          {items.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-surface"
            >
              <div
                className={cn(
                  'w-10 h-10 rounded-md grid place-items-center text-xs font-bold flex-shrink-0',
                  r.tipo === 'pdf' ? 'bg-danger-soft text-red-700' : 'bg-info-soft text-sky-700',
                )}
              >
                {r.tipo === 'pdf' ? 'PDF' : 'IMG'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-text truncate">
                  {r.nombreOriginal}
                </div>
                <div className="text-[11px] text-text-muted">
                  {(r.tamanoBytes / 1024).toFixed(0)} KB ·{' '}
                  {formatShortDateEs(r.subidoEn.slice(0, 10))} ·{' '}
                  {r.subidoPorNombre ?? r.subidoPorId}
                  {r.descripcion !== undefined && r.descripcion !== '' ? (
                    <> · {r.descripcion}</>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleView(r.id, r.nombreOriginal)}
                className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold hover:border-primary hover:text-primary"
              >
                Ver
              </button>
              <button
                type="button"
                onClick={() => handleDelete(r.id)}
                className="px-3 py-1.5 rounded-md border border-border text-xs font-semibold text-danger hover:bg-danger-soft hover:border-danger"
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

